import { ChangeEvent, FC, useRef, useState } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB, same limit as the shared uploader

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
};

/**
 * Picks merger documents before the merger exists. The shared uploader needs an entity id to
 * upload to, so on create the files are held here and uploaded once the create returns the id.
 */
export const PendingFiles: FC<Props> = ({ files, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const addFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = "";
    const tooBig = picked.filter((file) => file.size > MAX_BYTES);
    setSizeError(
      tooBig.length
        ? `${tooBig.map((f) => f.name).join(", ")} exceeds 50 MB.`
        : null
    );
    const known = new Set(files.map((f) => `${f.name}-${f.size}`));
    onChange([
      ...files,
      ...picked.filter(
        (file) =>
          file.size <= MAX_BYTES && !known.has(`${file.name}-${file.size}`)
      )
    ]);
  };

  return (
    <Box>
      <input ref={inputRef} type="file" multiple hidden onChange={addFiles} />
      <Button
        variant="outlined"
        startIcon={<UploadFileIcon />}
        onClick={() => inputRef.current?.click()}
      >
        Add documents
      </Button>
      {sizeError && (
        <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
          {sizeError}
        </Typography>
      )}
      <Stack spacing={0.5} sx={{ marginTop: 1 }}>
        {files.map((file) => (
          <Box
            key={`${file.name}-${file.size}`}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {file.name}
            </Typography>
            <IconButton
              size="small"
              aria-label={`Remove ${file.name}`}
              onClick={() => onChange(files.filter((f) => f !== file))}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
